import {
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
    useColorMode,
    useDisclosure,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeError } from '@stripe/stripe-js';
import { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { BsChevronLeft } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { AuthenticatedComponent } from '../../auth/keycloak';
import config from '../../config';
import PageDataContext from '../../context/PageDataContext';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import {
    createPaymentIntent,
    getProductByType,
    ProductDef,
    RealProduct,
    TwoFactorError,
    WrapperLicense,
} from '../../service/inventory';
import { getUserPrivateProfile, UserProfile } from '../../service/social';
import { stripeAppearenceOptions } from '../../service/stripe';
import { CheckoutForm } from '../storeComponents/CheckoutForm';
import { CurrencyListItem } from '../storeComponents/CurrencyListItem';
import { TwoFactorPermission } from '../storeComponents/TwoFactorPermission';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe(config.stripePublicKey);

interface BuyCurrencyProps {
    onPurchase(err: StripeError | undefined): void;
    refreshFlag?: boolean;
    loadCallback?: () => void;
    quantityCallback?: (total: number) => void;
}

export const BuyCurrency: AuthenticatedComponent<BuyCurrencyProps> = ({
    keycloak,
    onPurchase,
    loadCallback,
}) => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [virtualPackages, setPackages] = usePageDataCache<ProductDef[]>('virtualPackages', []);
    const [clientSecret, setClientSecret] = useState('');
    const [isOpen2fa, setIsOpen2fa] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductDef | undefined>();
    const [myProfile, setMyProfile] = useSessionStorage<UserProfile | undefined>(
        'myProfile',
        undefined,
    );
    const { colorMode } = useColorMode();

    useEffect(() => {
        if (!isLoadFromCache) {
            getPackages();

            async function getProfileInfo() {
                const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
                setMyProfile(profile);
            }
            if (myProfile === undefined) {
                getProfileInfo();
            }
        } else {
            if (loadCallback) loadCallback();
        }
    }, []);

    const getPackages = async () => {
        setPackages([]);
        try {
            const packages = await getProductByType(RealProduct, WrapperLicense, keycloak.token);
            const currency = [];
            for (let i = 0; i < packages.length; i++) {
                for (let j = 0; j < packages[i].items.length; j++) {
                    const item = packages[i].items[j].item_def;
                    if (item && item.item_type === 'streamsix_currency' && !item.is_subscription) {
                        currency.push(packages[i]);
                        break;
                    }
                }
            }
            setPackages(currency);
        } catch (err: any) {
            console.error(err);
            setPackages([]);
        } finally {
            if (loadCallback) loadCallback();
        }
    };

    const onPackageClick = async (product: ProductDef) => {
        try {
            const intent = await createPaymentIntent(product.product_id, keycloak.token);
            setClientSecret(intent.client_secret);
            setSelectedProduct(product);
            onOpen();
        } catch (err: any) {
            console.log('Unable to complete purchase: ', err.message);
            if (err.message === TwoFactorError) {
                setIsOpen2fa(true);
            }
        }
    };
    const appearance = stripeAppearenceOptions(colorMode);

    const elementOptions = {
        clientSecret,
        appearance,
    };

    const renderModal = () => (
        <Modal
            closeOnOverlayClick={true}
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            scrollBehavior={'inside'}
        >
            <ModalOverlay className="modal-overlay" />
            <ModalContent className="modal-content">
                <ModalHeader className={'purchase-modal-header'}>
                    <Flex>
                        <Flex className={'purchase-drawer-title'}>
                            <Text>Purchase</Text>
                        </Flex>
                        <Flex className="wallet-button">
                            <Link to="/dashboard">
                                <Button className={'wallet-nav'}>My Wallet</Button>
                            </Link>
                        </Flex>
                    </Flex>
                </ModalHeader>
                <ModalBody className={'purchase-modal-body'}>
                    {clientSecret && (
                        <Elements options={elementOptions} stripe={stripePromise}>
                            <CheckoutForm
                                clientSecret={clientSecret}
                                keycloak={keycloak}
                                product={selectedProduct}
                                email={myProfile?.email || ''}
                                onFormClose={(err) => {
                                    onClose();
                                    onPurchase(err);
                                }}
                            />
                        </Elements>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );

    const renderDrawer = () => (
        <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader className={'purchase-drawer-header'}>
                    <Flex>
                        <Flex className={'purchase-drawer-close-button'}>
                            <BsChevronLeft onClick={onClose} />
                        </Flex>
                        <Flex className={'purchase-drawer-title'}>
                            <Text>Purchase</Text>
                        </Flex>
                        <Flex className="wallet-button">
                            <Link to="/dashboard/wallet">
                                <Button className={'wallet-nav'}>My Wallet</Button>
                            </Link>
                        </Flex>
                    </Flex>
                </DrawerHeader>

                <DrawerBody className={'purchase-drawer-body'}>
                    {clientSecret && (
                        <Elements options={elementOptions} stripe={stripePromise}>
                            <CheckoutForm
                                clientSecret={clientSecret}
                                keycloak={keycloak}
                                product={selectedProduct}
                                email={myProfile?.email || ''}
                                onFormClose={(err) => {
                                    onClose();
                                    onPurchase(err);
                                }}
                            />
                        </Elements>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );

    return (
        <>
            <Flex className={'currency-bundles-container'}>
                <Text className={'currency-bundles-title'}>Stream Gold Bundles:</Text>
                <Flex className={'currency-list'}>
                    {virtualPackages.map((pkg, i) => (
                        <CurrencyListItem
                            key={`virtual_package_${i}`}
                            virtualPackage={pkg}
                            keycloak={keycloak}
                            onClick={onPackageClick}
                        />
                    ))}
                </Flex>
            </Flex>
            {isMobile ? renderDrawer() : renderModal()}
            <TwoFactorPermission
                isOpen={isOpen2fa}
                onClose={() => {
                    setIsOpen2fa(false);
                }}
            />
        </>
    );
};
